// src/services/api.ts
// Axios instance with:
//   • Attach Bearer access token to every request
//   • 401 → silent refresh → retry original request
//   • Redirect to /login if refresh also fails

import axios, {
  AxiosError,
  InternalAxiosRequestConfig,
} from 'axios';
import { store } from 'store';
import { setToken, logout } from 'store/slices/authSlice';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// ── Axios instance ─────────────────────────────────────────────────────────────
export const api = axios.create({
  baseURL: `${BASE_URL}/api`,
  withCredentials: true,   // send httpOnly refresh cookie automatically
  timeout: 15_000,
});

// CSRF double-submit companion cookie (see backend middleware/csrf.ts) —
// non-httpOnly by design so this can read it back and echo it as a header.
// Only /auth/refresh and /auth/logout actually check it; attaching it to
// every request is a harmless no-op everywhere else.
export function getCsrfToken(): string | null {
  const match = document.cookie.match(/(?:^|; )om_csrf=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : null;
}

// ── Request interceptor — attach access token + CSRF token ─────────────────────
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = store.getState().auth.accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const csrfToken = getCsrfToken();
  if (csrfToken) {
    config.headers['X-CSRF-Token'] = csrfToken;
  }
  return config;
});

// ── Silent refresh logic ───────────────────────────────────────────────────────
let isRefreshing = false;
let waitingQueue: Array<{
  resolve: (token: string) => void;
  reject:  (err: unknown) => void;
}> = [];

function processQueue(error: unknown, token: string | null = null): void {
  waitingQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else       resolve(token!);
  });
  waitingQueue = [];
}

// ── Response interceptor — handle 401 ─────────────────────────────────────────
api.interceptors.response.use(
  (response) => response,    // pass through 2xx responses unchanged

  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // Only intercept 401s that represent an *expired session* — i.e. the
    // request actually carried a bearer token when it was sent. A 401 from a
    // request that never had one (login with the wrong password, MFA verify,
    // etc.) means "rejected on its own merits", not "token expired"; silently
    // refreshing then retrying (or worse, hard-redirecting to /login mid
    // login-attempt) would just wipe out the error the user needs to see.
    const isUnauthorized = error.response?.status === 401;
    const isRefreshEndpoint = originalRequest.url?.includes('/auth/refresh');
    const alreadyRetried = originalRequest._retry === true;
    const hadToken = !!originalRequest.headers?.Authorization;

    if (!isUnauthorized || isRefreshEndpoint || alreadyRetried || !hadToken) {
      return Promise.reject(error);
    }

    // ── Multiple concurrent requests that all get 401 ──
    // Queue them all, resolve after ONE refresh call
    if (isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        waitingQueue.push({ resolve, reject });
      })
        .then((newToken) => {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api(originalRequest);
        })
        .catch((err) => Promise.reject(err));
    }

    // ── First 401 — attempt silent refresh ────────────────────────────────────
    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const { data } = await axios.post(
        `${BASE_URL}/api/auth/refresh`,
        {},
        {
          withCredentials: true,   // send httpOnly cookie
          headers: { 'X-CSRF-Token': getCsrfToken() || '' },
        }
      );

      const newToken: string = data.accessToken;

      // Update Redux store with new token
      store.dispatch(setToken(newToken));

      // Resolve all queued requests
      processQueue(null, newToken);

      // Retry the original request with new token
      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return api(originalRequest);

    } catch (refreshError) {
      // Refresh failed — force logout
      processQueue(refreshError, null);
      store.dispatch(logout());
      window.location.href = '/login';
      return Promise.reject(refreshError);

    } finally {
      isRefreshing = false;
    }
  }
);
